import React, { useEffect, useState } from 'react';
import { GetExcelHeadersByOption } from '../../reduxToolkit/Files/FilesApi';
import { useDispatch, useSelector } from 'react-redux';
import pdf_image from '../../assets/images/pdf.svg';
import ic_excel from '../../assets/images/ic-excel.svg';
import OptionModal from '../../pages/Modals/OptionModal';
import ButtonDropdown from "./ButtonDropdown";
import {useTranslation} from "react-i18next";
import {ExportIcon} from "./IconButtons";
import GenericModal from '../../pages/Modals/GenericModal';
/**
 * @email kpozos@ibl.mx
 * @author Karen Yunuen Pozos Arias
 * @title Full Stack Developer
 *
 * ExportDataBtn component renders a dropdown button that allows users to export data
 * in either Excel or PDF format. It optionally supports a custom API call for exporting.
 *
 * @component
 * @param {String} [id='btn_edb_export'] - The HTML id attribute for the export button (required).
 * @param {Object} body - The body data passed to the modal when exporting (required).
 * @param {String} option - The selected export module name (required).
 * @param {string} [searchElement] - Optional search input value used for export filtering (required).
 * @param {Number} moduleId - Module identifier for which the export is being performed (required).
 * @param {boolean} [exportAttendance] - Flag to indicate if the export is for attendance (required).
 * @param {boolean} [search] - Flag used in the export modal to identifier if search field has value (required).
 * @param {function} [callApi=null] - Custom function to execute instead of the default API call. It receives `isExcel` as parameter (optional).
 * @param {String} [classes='btn-icon-export'] Additional CSS classes applied to the dropdown button for styling.
 * @param {boolean} [showHeadersModal=true] - Flag that controls whether the export headers modal.
 * @param state setDisplayModal: State management to ensure only one modal is visible at a time.
 * is shown when using a custom API function (`callApi`)
 * @param contractorSupplier Flag that determines whether contractorId and supplierId
 * are included in the export request body. When true, both identifiers are set and sent to the export modal.
 * @param {Number} [totalElements=0] - Total amount of records that will be exported. When it is greater than
 * `confirmationLimit`, a confirmation modal is shown before starting the export. When it is not sent, the amount
 * returned by the last search of the same model is used (optional).
 * @param {Number} [confirmationLimit=5] - Amount of records from which the confirmation modal is required (optional).
 * @returns {JSX.Element} A button dropdown with Excel and PDF options and a modal for configuration.
 */

const ExportDataBtn = ({
                           id='btn_edb_export',
                           body,
                           option,
                           searchElement,
                           moduleId,
                           exportAttendance,
                           search,
                           callApi = null,
                           showHeadersModal=true,
                           classes='btn-icon-export',
                           contractorSupplier= true,
                           setDisplayModal= null,
                           totalElements= 0,
                           confirmationLimit= 30000
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { lastSearchTotal } = useSelector((state) => state.SearchSlice);
    const [showModal, setShowModal] = useState(false);
    const [excel, setExcel] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingExcel, setPendingExcel] = useState(false);

    const lastSearchMatchesOption = `${lastSearchTotal?.option}` === `${option}`;

    // Records to export: the amount sent by the module or, when it is not sent,
    // the one returned by the last search performed over the same model.
    const recordsToExport = totalElements
        || (lastSearchMatchesOption ? lastSearchTotal?.totalElements : 0);

    // The confirmation is only meant for advanced searches: that is where the user narrowed the
    // data down with filters and can still review them before generating a large file.
    const advancedSearchActive = lastSearchMatchesOption && !!lastSearchTotal?.fromAdvancedSearch;

    /**
     * Handles the default export action using the standard endpoint to get Excel or PDF headers.
     * This function is triggered when no custom API call (`callApi`) is provided.
     *
     * @function
     * @param {boolean} isExcel - Flag indicating whether the export should be in Excel format (true) or PDF format (false).
     * @returns {void}
     */
    const handleDownloadOption = (isExcel) => {
        setExcel(isExcel);
        setShowModal(true);
        dispatch(GetExcelHeadersByOption({ option: option.toString(), isExcel }));
    };

    /**
     * Handles export when a custom API function is provided through the `callApi` prop.
     * Opens the modal and passes the format type to the custom export function.
     *
     * @function
     * @param {boolean} isExcel - Flag indicating whether the export should be in Excel format (true) or PDF format (false).
     * @returns {void}
     */
    const handleCustomDownload = (isExcel) => {
        setExcel(isExcel);
        if (showHeadersModal) setShowModal(true);
        callApi(isExcel);
    };

    /**
     * Starts the export flow with the selected format, using the custom API function
     * when it is provided or the default endpoint otherwise.
     *
     * @function
     * @param {boolean} isExcel - Flag indicating whether the export should be in Excel format (true) or PDF format (false).
     * @returns {void}
     */
    const startExport = (isExcel) => (callApi !== null ? handleCustomDownload(isExcel) : handleDownloadOption(isExcel));

    /**
     * Requests the export of the selected format. When an advanced search is active and the amount
     * of records to export is greater than `confirmationLimit`, a confirmation modal is shown first,
     * so the user can review the information before generating a large file. A plain listing or a
     * simple search exports straight away.
     *
     * @function
     * @param {boolean} isExcel - Flag indicating whether the export should be in Excel format (true) or PDF format (false).
     * @returns {void}
     */
    const requestExport = (isExcel) => {
        if (advancedSearchActive && recordsToExport > confirmationLimit) {
            setPendingExcel(isExcel);
            setShowConfirmationModal(true);
            return;
        }
        startExport(isExcel);
    };

    /**
     * Hides other active modals when the export modal is opened,
     * ensuring only one modal is visible at a time.
     */
    useEffect(() => {
        if (showModal && setDisplayModal) {
            setDisplayModal('d-none');
        }
    }, [showModal]);

    return (
        <>
            <ButtonDropdown
                id={id}
                classes={classes}
                trans={'export_data'}
                spliName={false}
                icon={ExportIcon}
                options={[
                    {
                        id: 'mim_edb_excel',
                        option: t('Excel'),
                        img: ic_excel,
                        action: () => requestExport(true),
                    },
                    {
                        id: 'mim_edb_pdf',
                        option: t('PDF'),
                        img: pdf_image,
                        action: () => requestExport(false),
                    },
                ]}
            />

            {showConfirmationModal && (
                <GenericModal
                    title={'confirm_export'}
                    buttonConfirm={'continue'}
                    show={showConfirmationModal}
                    onHide={() => setShowConfirmationModal(false)}
                    onConfirm={() => startExport(pendingExcel)}
                >
                    <div className={"col-12"}>
                        <p className={"text-center ibl-font-w-600 font-color-general-950 fs-14px"}>
                            {t('you_are_about_to_export_records_this_process_may_take_a_while', { total: recordsToExport })}
                        </p>
                    </div>
                    <div className={"col-12 mt-2"}>
                        <p className={"text-center fs-14px"}>
                            {t('if_that_is_not_what_you_need_review_the_information_you_want_to_export')}
                        </p>
                    </div>
                </GenericModal>
            )}

            {showModal && (
                <OptionModal
                    body={body}
                    moduleId={moduleId}
                    show={showModal}
                    onHide={() => {
                        setShowModal(false);
                        if (setDisplayModal) setDisplayModal('');
                    }}
                    excel={excel}
                    option={option}
                    searchElement={searchElement}
                    exportAttendance={exportAttendance}
                    search={search}
                    contractorSupplier={contractorSupplier}
                />
            )}
        </>
    );
};

export default ExportDataBtn;
